<template>
  <div>
    <theater-admin-ticket-office-report
      v-if="isTicketOfficeReport()"
      :report="report"
      :organization="organization"
      :from="from"
      :to="to"
      :filter-labels="filterLabels"
    />
    <theater-admin-performance-report
      v-else
      :report="report"
      :organization="organization"
      :from="from"
      :to="to"
      :filter-labels="filterLabels"
    />
  </div>
</template>

<script>
import { GET_THEATER_ADMIN_REPORT } from '@/graphql/purchase.graphql'
import moment from 'moment'
import { GET_ORGANIZATION_BY_SLUG } from '../../../graphql/organization.graphql'
import { GET_ORGANIZATION_EVENT_TEMPLATES } from '@/graphql/eventTemplate.graphql'
import TheaterAdminTicketOfficeReport from './TheaterAdminTicketOfficeReport.vue'
import TheaterAdminPerformanceReport from './TheaterAdminPerformanceReport.vue'
import { GET_BOX_OFFICE_TICKET_SELLERS } from '@/graphql/user.graphql'

export default {
  components: {
    TheaterAdminTicketOfficeReport,
    TheaterAdminPerformanceReport,
  },
  props: {
    organizationSlug: String,
  },
  data() {
    return {
      filters: {},
      report: null,
      organization: null,
      from: '',
      to: '',
      filterLabels: [],
    }
  },
  async mounted() {
    this.filters = { ...this.$route.query }
    await this.fetchReport()
    if (this.isTicketOfficeReport()) {
      await this.fillEmptySalePoints()
    }
    await this.fetchOrganizationData()
    await this.fetchFilterLabels()
  },
  methods: {
    async fetchOrganizationData() {
      const { data } = await this.$apollo.query({
        query: GET_ORGANIZATION_BY_SLUG,
        variables: { organizationSlug: this.organizationSlug },
      })
      this.organization = data && data.getOrganizationBySlug
    },
    async fetchReport() {
      const {
        from,
        to,
        eventTemplateIds,
        eventIds,
        venues,
        saleType,
        paymentType,
        ticketSellerIds,
        periodType,
      } = this.filters

      if (from) {
        this.from = moment(from).format('DD.MM.YYYY.')
      }
      if (to) {
        this.to = moment(to).format('DD.MM.YYYY.')
      }

      const variables = {
        organizationSlug: this.organizationSlug,
        from,
        to,
        eventTemplateIds: this.normalizeArray(eventTemplateIds),
        eventIds: this.normalizeArray(eventIds),
        locations: this.normalizeArray(venues),
        saleType,
        paymentType,
        ticketSellerIds: this.normalizeNumberArray(ticketSellerIds),
        reportType: this.filters.reportType,
        periodType,
      }

      const { data } = await this.$apollo.query({
        query: GET_THEATER_ADMIN_REPORT,
        variables,
        fetchPolicy: 'no-cache',
      })
      this.report = data && data.getTheaterAdminReport
    },
    async fillEmptySalePoints() {
      if (!this.filters.saleType || this.filters.saleType === 'OFFLINE') {
        const { data } = await this.$apollo.query({
          query: GET_BOX_OFFICE_TICKET_SELLERS,
          variables: {
            organizationSlug: this.organizationSlug,
          },
        })
        const ticketSellers =
          data &&
          data.getBoxOfficeTicketSellers.filter(ts => this.filters.ticketSellerIds.includes(ts.id))
        const salePoints = this.report.ticketsSoldGrouped.map(sp => sp.salePoint)
        const missingSalePoints = ticketSellers.filter(
          ts => !salePoints.includes(`${ts.firstName} ${ts.lastName}`),
        )
        this.report.ticketsSoldGrouped = [
          ...this.report.ticketsSoldGrouped,
          ...missingSalePoints.map(ts => ({
            salePoint: `${ts.firstName} ${ts.lastName}`,
            eventGroups: [],
          })),
        ]
      }
      if (!this.filters.saleType || this.filters.saleType === 'ONLINE') {
        const onlineTicketSalePoint = this.report.ticketsSoldGrouped.find(
          sp => sp.salePoint === 'Online',
        )
        if (!onlineTicketSalePoint) {
          this.report.ticketsSoldGrouped.push({
            salePoint: 'Online',
            eventGroups: [],
          })
        }
      }
    },
    async fetchFilterLabels() {
      const [{ data: templatesData }, { data: sellersData }] = await Promise.all([
        this.$apollo.query({
          query: GET_ORGANIZATION_EVENT_TEMPLATES,
          variables: { organizationSlug: this.organizationSlug },
        }),
        this.$apollo.query({
          query: GET_BOX_OFFICE_TICKET_SELLERS,
          variables: { organizationSlug: this.organizationSlug },
        }),
      ])
      const eventTemplates = (templatesData && templatesData.getOrganizationEventTemplates) || []
      const events = eventTemplates.flatMap(et => et.events || [])
      const ticketSellers = (sellersData && sellersData.getBoxOfficeTicketSellers) || []

      const eventTemplateIds = (this.normalizeArray(this.filters.eventTemplateIds) || []).map(String)
      const eventIds = (this.normalizeArray(this.filters.eventIds) || []).map(String)
      const venues = this.normalizeArray(this.filters.venues) || []
      const ticketSellerIds = (this.normalizeArray(this.filters.ticketSellerIds) || []).map(String)
      const selectedTicketSellers = ticketSellers.filter(ts => ticketSellerIds.includes(String(ts.id)))
      const listOrAll = list => (list.length ? list.join(', ') : 'Sve')

      const isAllTicketSellers =
        !selectedTicketSellers.length || selectedTicketSellers.length === ticketSellers.length
      const reportTicketSellers = isAllTicketSellers ? ticketSellers : selectedTicketSellers
      const offlineSaleTypeLabel =
        reportTicketSellers.length === 1
          ? `${reportTicketSellers[0].firstName} ${reportTicketSellers[0].lastName}`
          : 'Offline'
      const saleTypeLabels = {
        ONLINE: 'Online',
        OFFLINE: offlineSaleTypeLabel,
        CORE_EVENT_TICKET_OFFICES: 'CoreEvent blagajne',
      }
      const paymentTypeLabels = { CASH: 'Gotovina', CARD: 'Kartica' }
      const periodTypeLabels = {
        SALES_REPORT: 'Po datumu prodaje',
        PERFORMANCES_REPORT: 'Po datumu izvedbe',
      }
      const periodTypeLabel = periodTypeLabels[this.filters.periodType] || 'Po datumu prodaje'
      const periodRange = this.from && this.to ? ` (${this.from} - ${this.to})` : ''

      const labels = [
        { label: 'Razdoblje', value: `${periodTypeLabel}${periodRange}` },
        { label: 'Dvorana', value: listOrAll(venues) },
        {
          label: 'Naziv predstave',
          value: listOrAll(
            eventTemplates.filter(et => eventTemplateIds.includes(String(et.id))).map(et => et.title),
          ),
        },
        {
          label: 'Izvedba',
          value: listOrAll(
            events
              .filter(ev => eventIds.includes(String(ev.id)))
              .map(ev => `${ev.title} - ${moment(ev.from).format('DD.MM.YYYY. HH:mm')}`),
          ),
        },
        { label: 'Način prodaje', value: saleTypeLabels[this.filters.saleType] || 'Sve' },
      ]
      if (this.filters.saleType !== 'ONLINE') {
        labels.push({
          label: 'Operater',
          value: isAllTicketSellers
            ? 'Sve'
            : selectedTicketSellers.map(ts => `${ts.firstName} ${ts.lastName}`).join(', '),
        })
      }
      labels.push({
        label: 'Način plaćanja',
        value: paymentTypeLabels[this.filters.paymentType] || 'Sve',
      })
      this.filterLabels = labels
    },
    normalizeArray(value) {
      if (!value) return undefined
      if (Array.isArray(value)) return value

      return String(value)
        .split(',')
        .map(v => v.trim())
        .filter(Boolean)
    },
    normalizeNumberArray(value) {
      const arr = this.normalizeArray(value)

      return arr ? arr.map(v => Number(v)).filter(n => !Number.isNaN(n)) : undefined
    },
    isTicketOfficeReport() {
      return this.filters.reportType === 'TICKET_OFFICE_REPORT'
    },
  },
}
</script>

<style scoped></style>
